import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Bell, BellOff, Plus, Trash2, Check } from "lucide-react";
import { toast } from "sonner";
import { trackEvent, ANALYTICS_EVENTS } from "@/lib/analytics";

type Rule = {
  id: string;
  name: string;
  keywords: string[];
  niches: string[];
  min_score: number;
  enabled: boolean;
};

type Notification = {
  id: string;
  rule_id: string;
  trend_id: string;
  read_at: string | null;
  created_at: string;
  trend?: { title: string; niche: string | null; total_score: number } | null;
};

export function TrendAlertsPanel() {
  const { user } = useAuth();
  const [rules, setRules] = useState<Rule[]>([]);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [name, setName] = useState("");
  const [keywords, setKeywords] = useState("");
  const [niches, setNiches] = useState("");
  const [minScore, setMinScore] = useState(60);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    if (!user) return;
    const [{ data: r }, { data: n }] = await Promise.all([
      supabase
        .from("trend_alert_rules")
        .select("id, name, keywords, niches, min_score, enabled")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false }),
      supabase
        .from("trend_alert_notifications")
        .select("id, rule_id, trend_id, read_at, created_at, trend:trends(title, niche, total_score)")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(20),
    ]);
    setRules((r as Rule[]) ?? []);
    setNotifications((n as unknown as Notification[]) ?? []);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [user]);

  useEffect(() => {
    if (!user) return;
    const channel = supabase
      .channel(`trend-alerts:${Math.random().toString(36).slice(2)}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "trend_alert_notifications",
          filter: `user_id=eq.${user.id}`,
        },
        () => load(),
      )
      .subscribe();
    return () => { supabase.removeChannel(channel); };
    // eslint-disable-next-line
  }, [user]);

  const splitList = (s: string) =>
    s.split(",").map((x) => x.trim()).filter(Boolean);

  const createRule = async () => {
    if (!user) return;
    if (!name.trim()) return toast.error("Name is required");
    setBusy(true);
    const { error } = await supabase.from("trend_alert_rules").insert({
      user_id: user.id,
      name: name.trim(),
      keywords: splitList(keywords),
      niches: splitList(niches),
      min_score: Math.max(0, Math.min(100, Number(minScore) || 0)),
      enabled: true,
    });
    setBusy(false);
    if (error) return toast.error(error.message);
    setName(""); setKeywords(""); setNiches(""); setMinScore(60);
    trackEvent(ANALYTICS_EVENTS.ALERT_RULE_CREATED, {
      min_score: Math.max(0, Math.min(100, Number(minScore) || 0)),
      keyword_count: splitList(keywords).length,
      niche_count: splitList(niches).length,
    });
    toast.success("Alert rule created");
    load();
  };

  const toggleRule = async (r: Rule) => {
    const { error } = await supabase
      .from("trend_alert_rules")
      .update({ enabled: !r.enabled })
      .eq("id", r.id);
    if (error) return toast.error(error.message);
    setRules((prev) => prev.map((x) => (x.id === r.id ? { ...x, enabled: !r.enabled } : x)));
  };

  const deleteRule = async (id: string) => {
    const { error } = await supabase.from("trend_alert_rules").delete().eq("id", id);
    if (error) return toast.error(error.message);
    setRules((prev) => prev.filter((r) => r.id !== id));
  };

  const markRead = async (id: string) => {
    const { error } = await supabase
      .from("trend_alert_notifications")
      .update({ read_at: new Date().toISOString() })
      .eq("id", id);
    if (error) return toast.error(error.message);
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, read_at: new Date().toISOString() } : n)),
    );
  };

  const markAll = async () => {
    if (!user) return;
    const { error } = await supabase
      .from("trend_alert_notifications")
      .update({ read_at: new Date().toISOString() })
      .eq("user_id", user.id)
      .is("read_at", null);
    if (error) return toast.error(error.message);
    load();
  };

  const unread = notifications.filter((n) => !n.read_at).length;

  return (
    <section className="rounded-xl border border-border bg-card p-5 space-y-5">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Bell className="size-4 text-primary" />
          <h2 className="font-display text-lg font-bold">Trend alerts</h2>
          {unread > 0 && (
            <span className="font-mono text-[10px] rounded-full bg-primary/10 text-primary px-2 py-0.5">
              {unread} new
            </span>
          )}
        </div>
        {unread > 0 && (
          <Button size="sm" variant="outline" onClick={markAll}>
            <Check className="size-3" /> Mark all read
          </Button>
        )}
      </div>

      {/* Rule creator */}
      <div className="rounded-lg border border-border/60 p-3 space-y-2">
        <div className="font-mono text-[10px] uppercase text-muted-foreground">New rule</div>
        <div className="grid sm:grid-cols-2 gap-2">
          <div>
            <Label className="text-xs">Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)}
              placeholder="e.g. AI productivity" maxLength={80} />
          </div>
          <div>
            <Label className="text-xs">Min score (0-100)</Label>
            <Input type="number" min={0} max={100} value={minScore}
              onChange={(e) => setMinScore(Number(e.target.value))} />
          </div>
          <div className="sm:col-span-2">
            <Label className="text-xs">Keywords (comma-separated, optional)</Label>
            <Input value={keywords} onChange={(e) => setKeywords(e.target.value)}
              placeholder="ai agent, automation, notion" />
          </div>
          <div className="sm:col-span-2">
            <Label className="text-xs">Niches (comma-separated, optional)</Label>
            <Input value={niches} onChange={(e) => setNiches(e.target.value)}
              placeholder="AI & tech, Finance & investing" />
          </div>
        </div>
        <Button size="sm" onClick={createRule} disabled={busy}>
          <Plus className="size-3" /> Add rule
        </Button>
      </div>

      {/* Rules list */}
      <div className="space-y-2">
        <div className="font-mono text-[10px] uppercase text-muted-foreground">Your rules</div>
        {rules.length === 0 ? (
          <p className="text-xs text-muted-foreground">No rules yet. Create one above.</p>
        ) : (
          <ul className="space-y-2">
            {rules.map((r) => (
              <li key={r.id} className="flex items-center gap-3 rounded border border-border/60 p-2 text-sm">
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate">{r.name}</div>
                  <div className="text-xs text-muted-foreground truncate">
                    score ≥ {r.min_score}
                    {r.keywords.length ? ` · kw: ${r.keywords.join(", ")}` : ""}
                    {r.niches.length ? ` · niches: ${r.niches.join(", ")}` : ""}
                  </div>
                </div>
                <Switch checked={r.enabled} onCheckedChange={() => toggleRule(r)} />
                <Button size="sm" variant="ghost" onClick={() => deleteRule(r.id)}
                  aria-label="Delete rule">
                  <Trash2 className="size-4" />
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Notifications */}
      <div className="space-y-2">
        <div className="font-mono text-[10px] uppercase text-muted-foreground">Recent matches</div>
        {notifications.length === 0 ? (
          <p className="text-xs text-muted-foreground flex items-center gap-1">
            <BellOff className="size-3" /> No matches yet.
          </p>
        ) : (
          <ul className="space-y-1.5">
            {notifications.map((n) => (
              <li key={n.id}
                className={`rounded border p-2 text-sm flex items-center gap-2 ${
                  n.read_at ? "border-border/60 bg-transparent" : "border-primary/40 bg-primary/5"
                }`}>
                <div className="flex-1 min-w-0">
                  <div className="truncate font-medium">{n.trend?.title ?? "(trend)"}</div>
                  <div className="text-xs text-muted-foreground">
                    {n.trend?.niche ?? "trend"} · score {n.trend?.total_score ?? "—"} ·{" "}
                    {new Date(n.created_at).toLocaleString()}
                  </div>
                </div>
                {!n.read_at && (
                  <Button size="sm" variant="ghost" onClick={() => markRead(n.id)}>
                    <Check className="size-3" />
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
